import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Plus, Ruler } from "lucide-react";
import { PRODUCTS, CATEGORIES } from "../../data/collection";
import { Reveal, EASE } from "./Reveal";
import Lookbook from "./Lookbook";
import SizeGuide from "./SizeGuide";

function ProductCard({ product, onOpen, priority }) {
  const tall = product.span === "tall";
  const vidRef = useRef(null);
  const [playing, setPlaying] = useState(false);

  const startTeaser = () => {
    const v = vidRef.current;
    if (!v) return;
    v.muted = true;
    v.defaultMuted = true;
    setPlaying(true);
    const p = v.play();
    if (p && p.catch) p.catch(() => {});
  };
  const stopTeaser = () => {
    const v = vidRef.current;
    setPlaying(false);
    if (v) {
      v.pause();
      try {
        v.currentTime = 0;
      } catch (e) {
        /* noop */
      }
    }
  };

  return (
    <Reveal y={60} delay={priority * 0.06}>
      <button
        data-testid={`product-card-${product.id}`}
        onClick={() => onOpen(product)}
        onMouseEnter={startTeaser}
        onMouseLeave={stopTeaser}
        className="group relative block w-full overflow-hidden bg-neutral-100 text-left"
      >
        <div
          className={`relative w-full overflow-hidden ${
            tall ? "aspect-[3/4] md:aspect-[3/4.6]" : "aspect-[4/5] md:aspect-[4/3.4]"
          }`}
        >
          <img
            src={product.cover}
            alt={product.name}
            loading={priority < 2 ? "eager" : "lazy"}
            className="h-full w-full object-cover object-center transition-transform duration-[900ms] ease-out group-hover:scale-105"
          />
          {product.video && (
            <video
              ref={vidRef}
              src={product.video}
              muted
              loop
              playsInline
              preload="none"
              data-testid={`product-teaser-${product.id}`}
              className={`absolute inset-0 h-full w-full object-cover object-center transition-opacity duration-700 ${
                playing ? "opacity-100" : "opacity-0"
              }`}
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent opacity-90" />

          {/* index */}
          <span className="absolute left-5 top-5 font-body text-[11px] uppercase tracking-[0.3em] text-white/80">
            {product.index}
          </span>

          {/* editorial verse overlay (layered classy writing) */}
          {product.verse && (
            <span className="pointer-events-none absolute inset-x-6 top-1/2 -translate-y-1/2 text-center font-serif-editorial italic text-white/80 text-lg md:text-2xl opacity-0 transition-opacity duration-700 group-hover:opacity-100 drop-shadow-[0_2px_16px_rgba(0,0,0,0.55)]">
              {product.verse}
            </span>
          )}

          {/* badge */}
          {product.badge && (
            <span className="absolute left-5 top-14 flex items-center gap-1.5 rounded-full border border-white/30 bg-black/40 backdrop-blur-md px-3 py-1 font-body text-[10px] uppercase tracking-[0.22em] text-[var(--kln-gold)]">
              <span className="text-[10px] leading-none">&#10022;</span>
              {product.badge}
            </span>
          )}

          {/* open cue */}
          <span className="absolute right-5 top-5 flex h-10 w-10 items-center justify-center rounded-full border border-white/40 text-white opacity-0 transition-all duration-500 group-hover:opacity-100 group-hover:rotate-90">
            <Plus className="h-4 w-4" strokeWidth={1.2} />
          </span>

          {/* caption block */}
          <div className="absolute inset-x-0 bottom-0 flex items-end justify-between p-5 md:p-6">
            <div>
              <h3 className="font-display text-3xl md:text-4xl font-light leading-none text-white">
                {product.name}
              </h3>
              <p className="mt-2 font-serif-editorial text-base italic text-white/70">
                {product.meaning}
              </p>
              <p className="mt-2 font-body text-xs uppercase tracking-[0.18em] text-white/90">
                &#8377;{product.price.toLocaleString("en-IN")}
              </p>
              {product.colours && (
                <p className="mt-2 font-serif-editorial text-xs italic text-white/55">
                  {product.colours}
                </p>
              )}
            </div>
            <span className="mb-1 flex items-center gap-1.5 font-body text-[10px] uppercase tracking-[0.25em] text-white/70 translate-y-2 opacity-0 transition-all duration-500 group-hover:translate-y-0 group-hover:opacity-100">
              Lookbook <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={1.4} />
            </span>
          </div>
        </div>
      </button>
    </Reveal>
  );
}

export default function Collection({ initialCategory = "onam" }) {
  const [active, setActive] = useState(null);
  const [guideOpen, setGuideOpen] = useState(false);
  const [category, setCategory] = useState(initialCategory);

  useEffect(() => {
    setCategory(initialCategory);
  }, [initialCategory]);

  // Open a specific product lookbook when other sections request it (e.g. Bestseller strip)
  useEffect(() => {
    const openHandler = (e) => {
      const id = e.detail;
      const p = PRODUCTS.find((x) => x.id === id);
      if (p) {
        if (p.category && p.category !== category) setCategory(p.category);
        setActive(p);
      }
    };
    const catHandler = (e) => {
      if (e.detail === "onam" || e.detail === "everyday" || e.detail === "bloom") setCategory(e.detail);
    };
    window.addEventListener("keilani:openProduct", openHandler);
    window.addEventListener("keilani:setCategory", catHandler);
    return () => {
      window.removeEventListener("keilani:openProduct", openHandler);
      window.removeEventListener("keilani:setCategory", catHandler);
    };
  }, [category]);

  const cat = CATEGORIES[category] || CATEGORIES.bloom;
  const filtered = PRODUCTS.filter((p) => p.category === category);

  return (
    <section
      id="collection"
      data-testid="collection"
      className="relative bg-white text-[#050505]"
    >
      <div className="mx-auto max-w-[1500px] px-6 md:px-12 lg:px-20 py-24 md:py-36">
        {/* header */}
        <div className="mb-12 md:mb-16 grid gap-8 md:grid-cols-12 md:items-end">
          <div className="md:col-span-7">
            <Reveal>
              <p className="font-body text-[11px] uppercase tracking-[0.4em] text-black/40" data-testid="collection-kicker">
                {cat.kicker}
              </p>
            </Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-5 font-display text-5xl md:text-7xl font-light leading-[0.92]" data-testid="collection-heading">
                {cat.headingA}
                <br />
                <span className="font-serif-editorial italic text-black/70">
                  {cat.headingB}
                </span>
              </h2>
            </Reveal>
          </div>
          <div className="md:col-span-5">
            <Reveal delay={0.1}>
              <p className="max-w-md font-body text-sm md:text-base font-light leading-relaxed text-black/60" data-testid="collection-caption">
                {cat.caption}
              </p>
              <p className="mt-4 max-w-md font-body text-sm font-light leading-relaxed text-black/45">
                {cat.body}
              </p>
              <button
                data-testid="collection-size-guide"
                onClick={() => setGuideOpen(true)}
                className="group mt-6 inline-flex items-center gap-2 font-body text-[11px] uppercase tracking-[0.24em] text-black/70 transition-colors hover:text-black"
              >
                <Ruler className="h-4 w-4" strokeWidth={1.3} />
                <span className="relative">
                  View size guide
                  <span className="absolute left-0 -bottom-0.5 h-px w-full origin-left scale-x-0 bg-black transition-transform duration-500 group-hover:scale-x-100" />
                </span>
              </button>
            </Reveal>
          </div>
        </div>

        {/* Bloom / Onam / Everyday toggle */}
        <Reveal>
          <div className="mb-10 flex flex-wrap items-center justify-between gap-4 border-b border-black/10 pb-6">
            <div
              data-testid="collection-toggle"
              role="tablist"
              className="relative inline-flex rounded-full border border-black/15 bg-white p-1"
            >
              {[CATEGORIES.bloom, CATEGORIES.onam, CATEGORIES.everyday].map((c) => (
                <button
                  key={c.id}
                  role="tab"
                  aria-selected={category === c.id}
                  data-testid={`toggle-${c.id}`}
                  onClick={() => setCategory(c.id)}
                  className="relative z-10 rounded-full px-3.5 sm:px-5 py-2 font-body text-[10px] sm:text-[11px] uppercase tracking-[0.2em] sm:tracking-[0.22em] transition-colors duration-300"
                >
                  {category === c.id && (
                    <motion.span
                      layoutId="toggle-pill"
                      transition={{ type: "spring", stiffness: 350, damping: 32 }}
                      className="absolute inset-0 -z-10 rounded-full bg-[#050505]"
                    />
                  )}
                  <span className={category === c.id ? "text-white" : "text-black/70"}>
                    <span className="sm:hidden">{c.id === "onam" ? "Onam" : c.id === "bloom" ? "Bloom" : "Everyday"}</span>
                    <span className="hidden sm:inline">{c.label}</span>
                  </span>
                </button>
              ))}
            </div>
            <span className="font-serif-editorial text-sm italic text-black/40">
              {filtered.length} piece{filtered.length === 1 ? "" : "s"}
            </span>
          </div>
        </Reveal>

        {/* editorial staggered grid */}
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 md:gap-8 md:auto-rows-auto" data-testid="collection-grid">
          <AnimatePresence mode="popLayout">
            {filtered.map((p, i) => (
              <motion.div
                key={p.id}
                layout
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.45, ease: EASE }}
                className={p.span === "tall" ? "md:row-span-2" : ""}
              >
                <ProductCard product={p} onOpen={setActive} priority={i} />
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </div>

      <Lookbook product={active} onClose={() => setActive(null)} />
      <SizeGuide open={guideOpen} onClose={() => setGuideOpen(false)} />
    </section>
  );
}

export { EASE };
